// sapDelivered.js
// -----------------------------------------------------------------------------
// Writes the time a driver finished a stop back to its sales orders in SAP,
// through the ASMX UpdateSoDelivered method -- the "delivered" time Shopee
// auto-ship reads. Runs from the driver's phone, because that is where เสร็จงาน
// happens and the phone already talks to the ASMX like every other page.
//
// Always called AFTER the Firestore stamp has committed, and never throws: the
// stamp in Firestore is the record of the visit, and SAP being down must not
// undo or delay it. It is silent to the driver on purpose -- a driver at a
// customer's door can do nothing about SAP. What is still owed stays marked
// `sapDelivered.pending` on the deliveryStop, and the next time the day loads
// retryOwedDelivered pays it, one stop at a time -- it reads the owed stops by
// query, so a stop finished on an earlier day is found too, and one finished more
// than 3 days ago is marked expired instead. Each SO is tried on the selected
// connection, then the other one. Writing the same time twice is
// harmless, so an overlap costs noise, not data.
//
// Only a stop finished through the driver app is covered: the legacy
// Driver/timestamp screen never sets `pending`, and a stop with no sales order
// (an RT-only pickup) owes nothing -- D.deliveredSos is empty for it.
// -----------------------------------------------------------------------------
import axios from 'axios'
import D from '@/components/driverDay'
import driverService from '@/components/driverService'
import config from '@/components/config'
import { reportError, reportWarning } from '@/components/report'

const TIMEOUT_MS = 10000

async function post (webPath, body) {
  const res = await axios.post(`${webPath}deliveryWebService.asmx/UpdateSoDelivered`, body, { timeout: TIMEOUT_MS })
  if (!res || !res.data || res.data.d !== 'true') {
    throw new Error('UpdateSoDelivered returned ' + (res && res.data && res.data.d))
  }
}

// Returns the SOs SAP did not take -- [] when all were written or nothing was
// owed. Never throws.
export async function pushDeliveredToSap (webPath, stop) {
  const failed = []
  try {
    const deliveredAt = D.deliveredAtText(stop.steps && stop.steps.finished)
    const sos = D.deliveredSos(stop)
    if (!deliveredAt || !sos.length) return []
    // The caller's selected connection first, then the other one: a phone on a
    // connection that is down would otherwise owe every SO until the next load.
    const paths = D.sapConnections(webPath, config.webPath, config.webPath2)
    for (const so of sos) {
      let written = false
      for (let i = 0; i < paths.length && !written; i++) {
        try {
          await post(paths[i], { so: Number(so), deliveredAt })
          written = true
        } catch (e) {
          if (i < paths.length - 1) reportWarning(`UpdateSoDelivered failed for SO ${so} on ${paths[i]}, trying ${paths[i + 1]}:`, e)
          else reportError(`UpdateSoDelivered failed for SO ${so} on ${paths[i]}:`, e)
        }
      }
      if (!written) failed.push(so)
    }
    if (!failed.length) {
      try {
        await driverService.markSapDelivered(stop, { deliveredAt, sos })
      } catch (e) {
        reportError('markSapDelivered failed:', e)
      }
    }
  } catch (e) {
    reportError('pushDeliveredToSap failed:', e)
  }
  return failed
}

let retrying = false

// Fire-and-forget: loads the stops this driver finished that SAP still owes
// (finished in the last 3 days), pays each one at a time, and ignores a call made
// while a run is still going.
export function retryOwedDelivered (webPath) {
  if (retrying) return
  retrying = true
  ;(async () => {
    const stops = await driverService.loadOwedDelivered()
    for (const stop of stops.filter(D.owesSapDelivered)) await pushDeliveredToSap(webPath, stop)
  })().catch(e => reportError('retryOwedDelivered failed:', e))
    .then(() => { retrying = false })
}

export default { pushDeliveredToSap, retryOwedDelivered }
